import { AfterViewChecked, Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { Moment } from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of, Subject } from 'rxjs';
import { debounceTime, finalize, first, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Permission } from 'src/app/common/enums';
import { Messages } from 'src/app/common/enums/messages';
import { replaceComma } from 'src/app/common/utils/gross-value-utils';
import { CreateOrUpdateDelegationRequest } from 'src/app/contracts/requests/create-or-update-delegation-request';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { DelegationService } from 'src/app/data/delegation.service';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { WorkerService } from 'src/app/data/worker.service';
import { Country } from 'src/app/models/Country';
import { DictionaryItem } from 'src/app/models/DictionaryItem';
import { AdvancePayment } from 'src/app/models/dtos/advance-payment';
import { DelegationDto } from 'src/app/models/dtos/delegation-dto';
import { InternalWorkerAgreementDto } from 'src/app/models/dtos/internal-worker-agreement-dto';
import { CurrencyEnum } from 'src/app/models/enums/currency-enum';
import { DelegationStatusEnum } from 'src/app/models/enums/delegation-status-enum';
import { ConfirmDialogComponent } from 'src/app/shared/messages/confirm-dialog/confirm-dialog.component';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';

@Component({
  selector: 'app-delegation-form',
  templateUrl: './delegation-form.component.html',
  styleUrls: ['./delegation-form.component.scss'],
  standalone: false
})
export class DelegationFormComponent implements OnInit, AfterViewChecked, OnDestroy {
  data = inject<{
    workerId: number;
    recordId: number;
  }>(MAT_DIALOG_DATA);
  private formBuilder = inject(UntypedFormBuilder);
  private dialogRef = inject<MatDialogRef<DelegationFormComponent>>(MatDialogRef);
  private workerService = inject(WorkerService);
  private dictionaryService = inject(DictionaryService);
  private delegationService = inject(DelegationService);
  private spinner = inject(NgxSpinnerService);
  private snackbar = inject(SnackBarService);
  private dialog = inject(MatDialog);
  private translateService = inject(TranslateService);
  private authService = inject(AuthService);

  listOfCurrencies$: Observable<DictionaryItem[]> = this.dictionaryService.getCurrencies();
  listOfCountries$: Observable<DictionaryItem[]> = this.dictionaryService.getCountries();
  listOfPaymentTypes$: Observable<DictionaryItem[]> = this.dictionaryService.getPaymentTypes();
  filteredWorkers$: Observable<DictionaryItem[]>;
  listOfCities$: Observable<string[]>;
  delegationPurposes$: Observable<DictionaryItem[]> = this.dictionaryService.getDelegationPurposes();
  transportTypes$: Observable<DictionaryItem[]> = this.dictionaryService.getTransportTypes();

  listOfWorkers: DictionaryItem[];
  formGroup: UntypedFormGroup;
  fullNameSubject = new BehaviorSubject<string>('');
  public advancePaymentCounter = 0;
  public readonly maximumAdvancePayments = 5;
  private readonly unsubscribe$ = new Subject<void>();
  private readonly timeBetweenInput = 300;
  private readonly minimumInputLetters = 2;
  public isEditMode = false;
  timepickerTimeFormat: number = 24;
  defaultTime: string = '00:00';

  agreementSubject = new BehaviorSubject<InternalWorkerAgreementDto[]>([]);
  agreements$ = this.agreementSubject.asObservable();
  public dataSource = new MatTableDataSource();
  private loggedWorkerId: number;


  constructor() {
    const data = this.data;

    this.isEditMode = !!data.workerId;
    this.formGroup = this.buildFormGroup();
  }

  get activeAgreement(): InternalWorkerAgreementDto {
    return this.formGroup.get('activeAgreement').value;
  }

  get advancePayments() {
    return this.formGroup.get('advancePayments') as UntypedFormArray;
  }

  get workerControl(): UntypedFormControl {
    return this.formGroup.get('worker') as UntypedFormControl;
  }

  get destinationCity(): UntypedFormControl {
    return this.formGroup.get('destinationCity') as UntypedFormControl;
  }

  get statusIdControl(): UntypedFormControl {
    return this.formGroup.get('statusId') as UntypedFormControl;
  }

  get statusName(): UntypedFormControl {
    return this.formGroup.get('statusName') as UntypedFormControl;
  }

  get startDateControl(): AbstractControl {
    return this.formGroup.get('startDate');
  }
  get startTimeControl(): AbstractControl {
    return this.formGroup.get('startTime');
  }
  get startDateTimeControl(): AbstractControl {
    return this.formGroup.get('startDateTime');
  }
  get endDateControl(): AbstractControl {
    return this.formGroup.get('endDate');
  }
  get endTimeControl(): AbstractControl {
    return this.formGroup.get('endTime');
  }
  get endDateTimeControl(): AbstractControl {
    return this.formGroup.get('endDateTime');
  }
  get activeAgreementControl(): AbstractControl {
    return this.formGroup.get('activeAgreement');
  }
  get startDate(): Date {
    return this.startDateControl.value;
  }
  get startTime(): string {
    return this.startTimeControl.value;
  }
  get startDateTime(): Date {
    return this.startDateTimeControl.value;
  }
  get endDate(): Date {
    return this.endDateControl.value;
  }
  get endTime(): string {
    return this.endTimeControl.value;
  }
  get endDateTime(): Date {
    return this.endDateTimeControl.value;
  }

  async ngOnInit(): Promise<void> {
    this.loggedWorkerId = await firstValueFrom(this.authService.getWorkerIdByAuthServerUserId());
    this.onWorkerChange();
    this.onDestinationCityChange();
    this.onStartDateChanges();
    this.onEndDateChanges();

    if (!this.data.workerId) {
      this.listOfWorkers = await this.getSubordinates();
      this.workerControl.enable();
      return;
    }

    this.fetchWorkerFullName(this.data.workerId);
    this.fetchAgremeents(this.data.workerId);
  }

  ngAfterViewChecked(): void {
    if (this.statusIdControl.value && this.statusIdControl.value !== DelegationStatusEnum.Draft) {
      this.formGroup.disable();
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }
  startDateFilter = (date: Moment): boolean =>
    this.formGroup && (!this.endDate || date?.isSameOrBefore(this.endDate)) && this.isDateValidAgainstActiveAgreementData(date);

  endDateFilter = (date: Moment): boolean =>
    this.formGroup && date?.isSameOrAfter(this.startDate) && this.isDateValidAgainstActiveAgreementData(date);

  async sendToApprovalClicked() {
    if (this.formGroup.invalid) {
      return;
    }

    if (this.startTime == this.defaultTime && this.endTime == this.defaultTime) {
      await this.openConfirmDialog().then((result) => {
        if (result) {
          this.sendToApproval();
        }
      });
    } else {
      this.sendToApproval();
    }
  }

  async saveAsDraft() {
    if (this.formGroup.invalid) {
      return;
    }

    if (this.startTime == this.defaultTime && this.endTime == this.defaultTime) {
      await this.openConfirmDialog().then((result) => {
        if (result) {
          this.save();
        }
      });
    } else {
      this.save();
    }
  }

  close = () => this.dialogRef.close();

  public addNewAdvancePayment(advancePayment: AdvancePayment = { Id: null, Value: null, PaymentTypeId: null, CurrencyId: null } as AdvancePayment) {
    this.advancePayments.push(this.newAdvancePayment(advancePayment));
    this.dataSource.data = this.advancePayments.controls;
    this.advancePaymentCounter++;
  }

  public deleteAdvancePayment(index: number) {
    this.advancePayments.removeAt(index);
    this.dataSource.data = this.advancePayments.controls;
    this.advancePaymentCounter--;
  }

  public displayValue(value: DictionaryItem): string | undefined {
    return value?.Name;
  }

  public isSaveAsDraftButtonVisible(): boolean {
    return (!this.statusIdControl.value ||
      this.statusIdControl.value === DelegationStatusEnum.Draft) &&
      (this.authService.hasPermission(Permission.Supervisor) ||
        this.loggedWorkerId === this.data.workerId);
  }

  public isSendButtonVisible(): boolean {
    return (!this.statusIdControl.value ||
      this.statusIdControl.value === DelegationStatusEnum.Draft) &&
      (this.authService.hasPermission(Permission.Supervisor) ||
        this.loggedWorkerId === this.data.workerId);
  }

  public isAdvancePaymentButtonVisible(): boolean {
    return (
      this.advancePaymentCounter < this.maximumAdvancePayments &&
      (!this.statusIdControl.value ||
        this.statusIdControl.value === DelegationStatusEnum.Draft ||
        this.statusIdControl.value === DelegationStatusEnum.PendingApproval)
    );
  }

  protected newAdvancePayment(advancePayment: AdvancePayment = null): UntypedFormGroup {
    const advancePaymentForm = this.formBuilder.group({
      Id: advancePayment?.Id,
      value: [advancePayment?.Value, [Validators.required]],
      currencyId: [advancePayment?.CurrencyId ?? CurrencyEnum.PLN, Validators.required],
      paymentTypeId: [advancePayment?.PaymentTypeId, Validators.required],
    });
    combineLatest([advancePaymentForm.valueChanges, this.listOfPaymentTypes$])
      .pipe(debounceTime(this.timeBetweenInput), takeUntil(this.unsubscribe$))
      .subscribe(([changedAdvancePayment, paymentTypes]) => {
        advancePaymentForm.get('value').updateValueAndValidity({ emitEvent: false });
      });
    return advancePaymentForm;
  }

  private async openConfirmDialog(): Promise<boolean> {
    return await firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          data: {
            title: Messages.ConfirmDelegationHoursTitle,
            message: Messages.ConfirmDelegationHoursMessage,
            confirmButtonLabel: this.translateService.instant(Messages.ConfirmDelegationHoursApprove),
            dismissButtonLabel: this.translateService.instant(Messages.ConfirmDelegationHoursCancel),
          },
        })
        .afterClosed(),
    );
  }

  private save() {
    if (this.formGroup.invalid) return;

    this.spinner.show();
    const request = this.createRequest();

    const action$ = !this.data.recordId
      ? this.delegationService.createDelegationRecord(this.data.workerId, request)
      : this.delegationService.updateDelegationRecord(this.data.workerId, this.data.recordId, request);
    const message = !this.data.recordId ? Messages.SuccessfullyCreatedDelegationRecord : Messages.SuccessfullyUpdatedDelegationRecord;

    action$
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe((_) => {
        this.snackbar.openSuccessSnackBar(message);
        this.dialogRef.close(true);
      });
  }

  private sendToApproval() {
    if (this.formGroup.invalid) return;

    const request = this.createRequest();
    this.spinner.show();
    const action$ = !this.data.recordId
      ? this.delegationService.createDelegationRecord(this.data.workerId, request)
      : this.delegationService.updateDelegationRecord(this.data.workerId, this.data.recordId, request);

    action$
      .pipe(
        first(),
        switchMap((delegationId) => {
          this.data.recordId = delegationId;

          return this.delegationService.sendToApproval(delegationId).pipe(first());
        }),
        finalize(() => this.spinner.hide()),
      )
      .subscribe((_) => this.closeDialogSuccessfully(Messages.SuccessfullySentToApprovalDelegtion));
  }

  private closeDialogSuccessfully = (message: string) => {
    this.snackbar.openSuccessSnackBar(message);
    this.dialogRef.close(true);
  };

  private buildFormGroup() {
    return this.formBuilder.group(
      {
        worker: [{ value: null, disabled: true }, [Validators.required]],
        startDate: [null, [Validators.required]],
        endDate: [null, [Validators.required]],
        activeAgreement: [null, Validators.required],
        country: [{ value: Country.Poland, disabled: true }, [Validators.required]],
        destinationCity: [null, [Validators.required]],
        delegationPurpose: [null, [Validators.required]],
        transportType: [null, [Validators.required]],
        advancePayments: this.formBuilder.array([]),
        statusId: [null],
        statusName: [null],
        startTime: [null, Validators.required],
        endTime: [null, Validators.required],
        startDateTime: [null],
        endDateTime: [null],
      },
      { validators: this.dateValidator },
    );
  }

  private dateValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const start = control.get('startDateTime');
    const end = control.get('endDateTime');

    return start.value !== null && end.value !== null && start.value < end.value ? null : { dateValid: true };
  };

  private onStartDateChanges() {
    const dateChanges$ = this.startDateControl.valueChanges.pipe(startWith(this.startDateControl.value));
    const timeChanges$ = this.startTimeControl.valueChanges.pipe(startWith(this.startTimeControl.value));

    combineLatest([dateChanges$, timeChanges$])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([selectedDate, selectedTime]) => {
        if (selectedDate && !selectedTime) {
          this.startTimeControl.setValue(this.defaultTime, { emitEvent: false });
          selectedTime = this.defaultTime;
        }

        if (this.startDate && this.startTime) {
          const dateTime = this.addTimeToDate(this.startDate, this.startTime);
          this.formGroup.get('startDateTime').setValue(dateTime, { emitEvent: true });
        }
      });
  }

  private onEndDateChanges() {
    const dateChanges$ = this.endDateControl.valueChanges.pipe(startWith(this.endDateControl.value));
    const timeChanges$ = this.endTimeControl.valueChanges.pipe(startWith(this.endTimeControl.value));

    combineLatest([dateChanges$, timeChanges$])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([selectedDate, selectedTime]) => {
        if (selectedDate && !selectedTime) {
          this.endTimeControl.setValue(this.defaultTime, { emitEvent: false });
          selectedTime = this.defaultTime;
        }

        if (this.endDate && this.endTime) {
          const dateTime = this.addTimeToDate(this.endDate, this.endTime);
          this.formGroup.get('endDateTime').setValue(dateTime, { emitEvent: false });
        }
      });
  }

  private addTimeToDate = (date: Date, time: string) => new Date(new Date(date).getTime()).setStringTime(time);

  private isDateValidAgainstActiveAgreementData(d: Moment) {
    return (
      this.activeAgreement &&
      (this.activeAgreement.EmploymentDateTo ? d.isSameOrBefore(this.activeAgreement.EmploymentDateTo) : true) &&
      d.isSameOrAfter(this.activeAgreement.EmploymentDateFrom)
    );
  }

  private fetchWorkerFullName(workerId) {
    this.workerService
      .getWorkerFullName(workerId)
      .pipe(first())
      .subscribe((fullname) => {
        return this.fullNameSubject.next(fullname);
      });
  }

  private fetchAgremeents(workerId) {
    const getOnlyOneAgreement = (agreements: InternalWorkerAgreementDto[]) => {
      const activeAgreements = agreements.filter(a => a.IsActive);

      return activeAgreements.length === 1 && activeAgreements[0]
        || agreements.length === 1 && agreements[0]
        || null;
    };

    this.agreements$ = this.workerService.getInternalWorkerAgreements(workerId).pipe(
      tap((agreements) => {
        this.agreementSubject.next(agreements);
        const agreement = getOnlyOneAgreement(agreements);

        if (agreement) {
          this.activeAgreementControl.setValue(agreement, { emitEvent: true });
        }
      }),
      tap((agreements) => {
        if (!!this.data.recordId) {
          setTimeout(() => this.fetchDelegationRecord(this.data.recordId, agreements));
        } else {
          this.patchFormGroupValues(
            { WorkerAgreementId: getOnlyOneAgreement(agreements)?.Id, AdvancePayments: [] } as DelegationDto,
            agreements,
          );
        }
      }),
    );
  }

  private fetchDelegationRecord(recordId: number, agreements: InternalWorkerAgreementDto[]) {
    this.spinner.show();
    this.delegationService
      .getDelegationById(this.data.workerId, recordId)
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe((record) => {
        this.patchFormGroupValues(record, agreements);
      });
  }

  private patchFormGroupValues(record: DelegationDto, agreements: InternalWorkerAgreementDto[]): void {
    this.formGroup.get('activeAgreement').setValue(
      agreements.find((a) => a.Id === record.WorkerAgreementId),
      { emitEvent: true },
    );

    this.formGroup.patchValue({
      countryId: record.CountryId,
      statusId: record.StatusId,
      statusName: record.StatusName,
      transportType: record.TransportTypeId,
      delegationPurpose: record.DelegationPurposeId,
      paymentTypeId: record.PaymentTypeId,
      destinationCity: record.CityName,
      advancePayments: record.AdvancePayments,
    });

    if (record.StartDate && record.EndDate) {
      this.formGroup.patchValue({
        startDateTime: new Date(record.StartDate),
        endDateTime: new Date(record.EndDate),
        startDate: new Date(record.StartDate).resetTime(),
        startTime: record.StartDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }),
        endDate: new Date(record.EndDate).resetTime(),
        endTime: record.EndDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }),
      });
    }

    this.formGroup.updateValueAndValidity();
    record.AdvancePayments.forEach((x) =>
      this.addNewAdvancePayment({ Id: x.Id, CurrencyId: x.CurrencyId, PaymentTypeId: x.PaymentTypeId, Value: x.Value } as AdvancePayment),
    );
  }

  private createRequest(): CreateOrUpdateDelegationRequest {
    const values = this.formGroup.getRawValue();
    let advancePayments = [];
    this.advancePayments.value.forEach((element) => {
      advancePayments.push({
        Id: element.Id,
        Value: replaceComma(element.value),
        CurrencyId: element.currencyId,
        PaymentTypeId: element.paymentTypeId,
      } as AdvancePayment);
    });

    return {
      StartDate: values.startDateTime,
      EndDate: values.endDateTime,
      WorkerAgreementId: values.activeAgreement.Id,
      CountryId: values.country,
      TransportTypeId: values.transportType,
      PurposeId: values.delegationPurpose,
      PaymentTypeId: values.delegationPurpose,
      CityName: values.destinationCity,
      AdvancePayments: advancePayments,
    };
  }

  private onWorkerChange() {
    this.filteredWorkers$ = this.workerControl.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
      debounceTime(this.timeBetweenInput),
      map((value) => (!!value?.Name ? this.filterWorkers(value.Name) : this.filterWorkers(value))),
    );
  }

  filterWorkers(value: string): DictionaryItem[] {
    if (value) {
      const filterValue = value.toLowerCase();
      return this.listOfWorkers.filter((item) => item.Name?.toLowerCase().includes(filterValue));
    } else {
      return this.listOfWorkers;
    }
  }

  private onDestinationCityChange() {
    this.listOfCities$ = this.destinationCity.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
      debounceTime(this.timeBetweenInput),
      switchMap((value: string) => {
        if (value && value.length > this.minimumInputLetters) {
          return this.dictionaryService.getAllCitiesNames(value);
        } else {
          return of(null);
        }
      }),
    );
  }

  private async getSubordinates() {
    return await firstValueFrom(this.workerService.getActiveSubordinatesBasicData());
  }

  public onWorkerSelection(worker) {
    return this.fetchAgremeents(worker.Id);
  }
}
